/**
 * Fudeto — web entry (v2.2 Joy Engine).
 *
 * Boot order:
 *   1. mount #app + critical CSS
 *   2. detect platform (TG / LINE / iOS / Android / PWA / Web / Discord)
 *   3. initialize i18n (resolves navigator.language → en / ja / zh-Hant / zh-Hans)
 *   4. initialize profile state from IndexedDB (or seed defaults)
 *   5. apply theme + a11y prefs
 *   6. register routes (core path eager; everything else lazy-imported)
 *   7. start router; first nav is always G1 home (v2.2 — 0 onboarding,
 *      first puzzle itself teaches per MASTERPIECE-v2.2 §0.2 I-14).
 *
 * Route layer follows MASTERPIECE-v2.2 §1.1 — ~20 routes, no
 * onboarding / fake IAP / multi-tier progression sub-pages.
 */
import { initI18n, t } from './i18n/runtime';
import { detectPlatform } from './platform/detect';
import { initPwa } from './platform/pwa';
import { initTg } from './platform/tg';
import { initZoomGuard } from './platform/zoom-guard';
import { initProfile } from './state/profile';
import { initSync } from './state/sync';
import { maybeMountCookieBanner } from './ui/cookie-banner';
import { type ScreenFactory, defineRoute, startRouter } from './ui/router';
import './styles/tokens.css';
import './styles/base.css';
import './styles/screens.css';

// Core path (eager — always reachable cold)
import home from './ui/screens/g1-home';
import play from './ui/screens/g2-play';
import solve from './ui/screens/g4-solve';

/** Lazy-imported screen — Vite emits a separate chunk per import path. */
function lazy(
  loader: () => Promise<Record<string, unknown>>,
  exportName = 'default',
): ScreenFactory {
  return async (ctx) => {
    const mod = await loader();
    const factory = mod[exportName] as ScreenFactory | undefined;
    if (!factory) throw new Error(`lazy screen: missing export ${exportName}`);
    return factory(ctx);
  };
}

function applyTheme(theme: string): void {
  document.documentElement.dataset['theme'] = theme;
}

function registerRoutes(): void {
  // ── Core game ─────────────────────────────────────────
  defineRoute('/', home);
  defineRoute('/play/:date', play);
  defineRoute('/solve/:date', solve);
  defineRoute(
    '/practice',
    lazy(() => import('./ui/screens/g3-practice')),
  );
  // FDT-O-015 — World Cities (post-Practice austere content tier).
  defineRoute(
    '/cities',
    lazy(() => import('./ui/screens/g6-world-cities')),
  );
  defineRoute(
    '/share/:date',
    lazy(() => import('./ui/screens/g5-share')),
  );
  defineRoute(
    '/review/:date',
    lazy(() => import('./ui/screens/review')),
  );

  // ── Extension (voluntary) ─────────────────────────────
  // Stats: single scrolling page — 4 blocks (Today/Streak/Country/Lifetime) per P-2.4
  defineRoute(
    '/stats',
    lazy(() => import('./ui/screens/stats')),
  );
  // Country Season leaderboard (voluntary national competition)
  defineRoute(
    '/leaderboard',
    lazy(() => import('./ui/screens/leaderboard'), 'countrySeasonHome'),
  );
  // IAP bottom sheet — streak_shield consumable + Fudeto Pro link (daily_skip + ads cut)
  defineRoute(
    '/iap/buy',
    lazy(() => import('./ui/screens/m2-iap-sheet')),
  );
  // Fudeto Pro subscription (v2.2 — replaces /iap/pro; no 7-day trial dark pattern)
  defineRoute(
    '/subscribe',
    lazy(() => import('./ui/screens/monetization'), 'subscribe'),
  );
  // Referral — voluntary share (Day 7+ gated by guard inside screen)
  defineRoute(
    '/invite',
    lazy(() => import('./ui/screens/referral'), 'inviteShare'),
  );

  // ── Settings ──────────────────────────────────────────
  defineRoute(
    '/settings',
    lazy(() => import('./ui/screens/settings'), 'settingsHome'),
  );
  defineRoute(
    '/settings/notifications',
    lazy(() => import('./ui/screens/settings'), 'notificationsScreen'),
  );
  defineRoute(
    '/settings/privacy',
    lazy(() => import('./ui/screens/settings'), 'privacyScreen'),
  );

  // ── OAuth bind ────────────────────────────────────────
  defineRoute(
    '/oauth/bind',
    lazy(() => import('./ui/screens/o5-oauth-bind')),
  );
  defineRoute(
    '/oauth/:provider',
    lazy(() => import('./ui/screens/o5-oauth-bind')),
  );

  // ── Legal (送審必要) ───────────────────────────────────
  defineRoute(
    '/legal/privacy',
    lazy(() => import('./ui/screens/legal'), 'privacy'),
  );
  defineRoute(
    '/legal/terms',
    lazy(() => import('./ui/screens/legal'), 'terms'),
  );

  // ── Progression ───────────────────────────────────────
  // Calendar tab destination (P1 Gem Calendar)
  defineRoute(
    '/calendar',
    lazy(() => import('./ui/screens/progression'), 'gemCalendar'),
  );
  // Profile tab destination (P2 Personal Stats)
  defineRoute(
    '/profile',
    lazy(() => import('./ui/screens/progression'), 'personalStats'),
  );

  // ── Edge states (only the ones still relevant) ────────
  defineRoute(
    '/error',
    lazy(() => import('./ui/screens/edge'), 'errorToast'),
  );
  defineRoute(
    '/maintenance',
    lazy(() => import('./ui/screens/edge'), 'maintenance'),
  );
}

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) throw new Error('#app missing');

  const platform = detectPlatform();
  document.documentElement.dataset['platform'] = platform.name;
  if (platform.name === 'telegram') initTg();
  if (platform.name === 'line') void import('./platform/liff').then((m) => m.initLiff());
  if (platform.name === 'web' || platform.name === 'pwa') initPwa();

  // Boot locale: profile override (if user explicitly chose one) wins over navigator.language.
  const store = await initProfile();
  const initialLang = store.get().localeOverride ?? navigator.language;
  await initI18n(initialLang);
  applyTheme(store.get().theme);
  store.subscribe((s) => applyTheme(s.theme));
  initSync();
  maybeMountCookieBanner(platform);

  // Arm the daily streak reminder (native only; no-op on web/PWA). The settings
  // toggle requests permission in-app and schedules directly, but a user can
  // still grant/revoke out-of-app in iOS Settings — so we (re-)arm BOTH on cold
  // boot AND every time the app returns to the foreground. Gated on notifAsked
  // so users who never engaged the toggle are never prompted;
  // scheduleStreakReminder no-ops when permission isn't granted or
  // LocalNotifications is unavailable.
  const armStreakReminder = (): void => {
    if (platform.name !== 'ios' && platform.name !== 'android') return;
    if (!store.get().notifAsked) return;
    // Parse 'HH:MM' defensively — fall back to 08:00 on any malformed value so a
    // corrupt persisted field can never schedule at NaN:NaN.
    const m = /^(\d{1,2}):(\d{2})$/.exec(store.get().reminderHour ?? '');
    const rh = m ? Number(m[1]) : 8;
    const rm = m ? Number(m[2]) : 0;
    void import('./platform/bridge').then(({ scheduleStreakReminder }) =>
      scheduleStreakReminder(
        { hour: rh, minute: rm },
        t('settings.notif_reminder_title'),
        t('settings.notif_reminder_body'),
      ),
    );
  };
  armStreakReminder();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') armStreakReminder();
  });

  // RevenueCat init — native only. Key is platform-specific (iOS vs Android).
  // Yves: paste keys via Vite env (VITE_RC_IOS_KEY / VITE_RC_ANDROID_KEY) before build.
  if (platform.name === 'ios' || platform.name === 'android') {
    const env = import.meta.env as Record<string, string | undefined>;
    const key = platform.name === 'ios' ? env['VITE_RC_IOS_KEY'] : env['VITE_RC_ANDROID_KEY'];
    if (key) {
      const { configureIAP, fetchProState } = await import('./platform/bridge');
      // Configure RC, then sync Pro entitlement from RC's verified customerInfo so
      // a paid subscriber unlocks Pro on every launch (and a lapsed one is
      // downgraded). null = RC momentarily unreachable → keep the current state.
      void configureIAP({ apiKey: key, appUserID: store.get().uid })
        .then(async (ok) => {
          if (!ok) return;
          const st = await fetchProState();
          if (st) store.set({ vipState: st });
          // Cross-device restore: pull the cloud snapshot for this RC account key
          // and merge (never reduces local progress), then keep the cloud copy
          // fresh on every profile change (debounced). Best-effort; silent on fail.
          const { cloudRestore, initCloudBackup } = await import('./state/cloud-restore');
          await cloudRestore();
          initCloudBackup();
        })
        // Never let an IAP/RC init hiccup surface as an uncaught rejection — the
        // game itself does not depend on RevenueCat being reachable.
        .catch(() => {});
    }
  }

  registerRoutes();

  // v2.3 FDT-O-001 — a brand-new player's first launch lands on the welcome
  // puzzle (a guaranteed 4-vertex warm-up that teaches by doing), not the daily.
  // Every later launch goes straight to G1 home (Wordle behavior). The gate
  // closes once welcome is solved/skipped (welcomeShown) or any daily is solved
  // (totalSolves>0). Deep links (e.g. a shared /play/:date) bypass this entirely.
  if (location.hash === '' || location.hash === '#') {
    const pf0 = store.get();
    const firstRun = pf0.totalSolves === 0 && !pf0.welcomeShown;
    history.replaceState(null, '', firstRun ? '#/play/welcome' : '#/');
  }
  startRouter(app);

  // Auto-reset accidental pinch-zoom back to fit (touch only; never disables
  // zoom outright — WCAG 1.4.4). The shell never intends pinch as input.
  initZoomGuard();

  // Remove inline boot splash once the app has mounted its first screen.
  // Uses a fade-out so the transition from splash → home is seamless.
  const splash = document.getElementById('boot-splash');
  if (splash) {
    splash.classList.add('fade-out');
    setTimeout(() => splash.remove(), 240);
  }
}

boot().catch((err) => {
  console.error('boot failed', err);
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = '<p style="text-align:center;padding:24px;">Unable to load Fudeto.</p>';
  }
});
